import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LayoutDashboard, ScanLine, BookOpen, Users, Settings, X, Wallet, Wrench, Package, HelpCircle, ReceiptText, PlayCircle } from 'lucide-react';
import { usePortalAuth, PortalPermission } from '@/hooks/usePortalAuth';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import HelpSupportDialog from '@/components/shared/HelpSupportDialog';
import { supabase } from '@/integrations/supabase/client';
import { PORTAL_FIX_REQUESTS_SEEN_KEY } from '@/components/portal/portalConstants';

interface NavItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  permission?: PortalPermission;
  ownerOnly?: boolean;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/portal/dashboard' },
  { label: 'Memorials', icon: BookOpen, path: '/portal/memorials' },
  { label: 'Install Medallion', icon: ScanLine, path: '/portal/upload-photo' },
  { label: 'Inventory', icon: Package, path: '/portal/inventory' },
  { label: 'Service Requests', icon: Wrench, path: '/portal/fix-requests' },
  { label: 'My Earnings', icon: Wallet, path: '/portal/earnings', permission: 'view_earnings' },
  { label: 'Rates & Pricing', icon: ReceiptText, path: '/portal/rates', ownerOnly: true },
  { label: 'Team', icon: Users, path: '/portal/team', permission: 'view_team' },
  { label: 'Training Videos', icon: PlayCircle, path: '/portal/training-videos' },
  { label: 'Settings', icon: Settings, path: '/portal/settings', permission: 'manage_settings' },
];

interface PortalSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const PortalSidebar = ({ isOpen, onClose }: PortalSidebarProps) => {
  const { isOwner, hasPermission, company } = usePortalAuth();
  const isMobile = useIsMobile();
  const [helpOpen, setHelpOpen] = useState(false);

  const { data: fixRequestCount = 0 } = useQuery({
    queryKey: ['portal-fix-requests-count', company?.id],
    queryFn: async () => {
      if (!company?.id) return 0;
      const { count, error } = await supabase
        .from('fix_requests')
        .select('id', { count: 'exact', head: true })
        .eq('monument_company_id', company.id);
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!company?.id,
    refetchInterval: 60 * 1000,
  });

  const location = useLocation();
  const isOnFixRequestsPage = location.pathname === '/portal/fix-requests';
  const lastSeenCount = company?.id
    ? parseInt(typeof window !== 'undefined' ? localStorage.getItem(PORTAL_FIX_REQUESTS_SEEN_KEY(company.id)) || '0' : '0', 10)
    : 0;
  const serviceRequestsUnread = isOnFixRequestsPage ? 0 : Math.max(0, fixRequestCount - lastSeenCount);

  const filteredItems = navItems.filter((item) => {
    // No permission required - show to everyone
    if (!item.permission && !item.ownerOnly) return true;
    // Owner-only items
    if (item.ownerOnly) return isOwner;
    // Permission-based items - owners have all permissions
    if (item.permission) return isOwner || hasPermission(item.permission);
    return true;
  });

  const handleNavClick = () => {
    if (isMobile) {
      onClose();
    }
  };

  const helpButton = (
    <div className="p-4 border-t border-border">
      <Button 
        variant="ghost" 
        className="w-full justify-start text-muted-foreground hover:text-foreground"
        onClick={() => setHelpOpen(true)}
      >
        <HelpCircle className="h-5 w-5 mr-3" />
        Help & Support
      </Button>
    </div>
  );

  // Mobile sidebar with overlay
  if (isMobile) {
    return (
      <>
        {/* Backdrop overlay */}
        {isOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 animate-fade-in"
            onClick={onClose}
          />
        )}

        {/* Sidebar */}
        <aside
          className={cn(
            'fixed top-0 left-0 h-full w-64 bg-card border-r border-border z-50 transition-transform duration-300 ease-in-out flex flex-col',
            isOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          <div className="flex items-center justify-between p-4 border-b border-border">
            <span className="font-semibold text-foreground">Menu</span>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
          <nav className="flex-1 p-4 space-y-1">
            {filteredItems.map((item) => {
              const badgeCount = item.path === '/portal/fix-requests' ? serviceRequestsUnread : 0;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={handleNavClick}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    )
                  }
                >
                  <item.icon className="h-5 w-5 shrink-0" />
                  <span className="flex-1 truncate">{item.label}</span>
                  {badgeCount > 0 && (
                    <Badge variant="destructive" className="h-5 min-w-5 px-1.5 text-xs shrink-0">
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </Badge>
                  )}
                </NavLink>
              );
            })}
          </nav>
          {helpButton}
        </aside>

        <HelpSupportDialog
          open={helpOpen}
          onOpenChange={setHelpOpen}
          portalType="monument"
          companyId={company?.id}
          companyName={company?.name}
        />
      </>
    );
  }

  // Desktop sidebar (always visible)
  return (
    <>
      <aside className="w-64 border-r border-border bg-card min-h-[calc(100vh-4rem)] hidden md:flex md:flex-col">
        <nav className="flex-1 p-4 space-y-1">
          {filteredItems.map((item) => {
            const badgeCount = item.path === '/portal/fix-requests' ? serviceRequestsUnread : 0;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  )
                }
              >
                <item.icon className="h-5 w-5 shrink-0" />
                <span className="flex-1 truncate">{item.label}</span>
                {badgeCount > 0 && (
                  <Badge variant="destructive" className="h-5 min-w-5 px-1.5 text-xs shrink-0">
                    {badgeCount > 99 ? '99+' : badgeCount}
                  </Badge>
                )}
              </NavLink>
            );
          })}
        </nav>
        {helpButton}
      </aside>

      <HelpSupportDialog
        open={helpOpen}
        onOpenChange={setHelpOpen}
        portalType="monument"
        companyId={company?.id}
        companyName={company?.name}
      />
    </>
  );
};

export default PortalSidebar;
